import { Dispatch } from 'redux';
import { flow, isEmpty, isNull, keys, pickBy } from 'lodash/fp';
import { PRODUCT_TYPE } from '@ahmdigital/constants';
import { RootStateOrAny } from 'react-redux';

import {
  BUILD_YOUR_COVER_INVALID_PRODUCT_TYPES,
  BUILD_YOUR_COVER_RESET_PRODUCT,
  BUILD_YOUR_COVER_SELECT_PRODUCT,
  BUILD_YOUR_COVER_SERVICES_CHANGE,
  BUILD_YOUR_COVER_STEP_CHANGE,
} from '../action-types';
import { IS_CHOOSE_YOUR_COVER } from '../../pages/product/util/use-is-from-choose-your-cover';
import { IS_CHOOSE_YOUR_EXTRAS_COVER } from '../../pages/product/util/use-is-from-choose-your-extras-cover';
import { JourneyProductType } from '../../pages/build-your-cover/types';
import { ProductType } from '../../types/product';
import { ProductType as ProductTypeType, Service } from '../../types/__codegen__/ahm-api/graphql';
import ProductService from '../../models/product';
import UrlService from '../../models/url';

export type BuildYourCoverProductType = `${ProductTypeType}`;

const validateSelectedProducts = (selectedProducts: Record<ProductTypeType, ProductType['id'] | null>) =>
  flow(pickBy(isNull), keys)(selectedProducts) as ProductTypeType[];

const setSelectedServices =
  (productType: BuildYourCoverProductType, selectedServices: Service['serviceId'][]) => (dispatch: Dispatch) =>
    dispatch({ productType, selectedServices, type: BUILD_YOUR_COVER_SERVICES_CHANGE });

const setOrUnsetSelectedProduct =
  (productType: BuildYourCoverProductType, selectedProductId: ProductType['id']) =>
  (dispatch: Dispatch, getState: RootStateOrAny) => {
    const {
      buildYourCover: { selectedProducts },
    } = getState();

    // If the product is already selected, deselect it
    if (selectedProducts[productType] === selectedProductId) {
      dispatch({ productType, selectedProductId, type: BUILD_YOUR_COVER_RESET_PRODUCT });
    } else {
      dispatch({ productType, selectedProductId, type: BUILD_YOUR_COVER_SELECT_PRODUCT });
      const newInvalidProductTypes = validateSelectedProducts(selectedProducts);
      dispatch({ newInvalidProductTypes, type: BUILD_YOUR_COVER_INVALID_PRODUCT_TYPES });
    }
  };

const submitReview =
  (
    selectedProducts: Record<ProductTypeType, ProductType['id'] | null>,
    router: any,
    journeyProductType?: JourneyProductType,
  ) =>
  (dispatch: Dispatch) => {
    const isFromChooseYourExtrasCover = journeyProductType === PRODUCT_TYPE.EXTRAS;
    // Validate that all product types are selected and set/clear invalid product types
    const productsToValidate: Record<ProductTypeType, ProductType['id'] | null> = isFromChooseYourExtrasCover
      ? ({ [ProductTypeType.Extras]: selectedProducts.extras } as Record<ProductTypeType, ProductType['id'] | null>)
      : selectedProducts;

    const invalidProductTypes = validateSelectedProducts(productsToValidate);
    dispatch({ invalidProductTypes, type: BUILD_YOUR_COVER_INVALID_PRODUCT_TYPES });

    if (isEmpty(invalidProductTypes)) {
      let cartProductId: ProductType['id'] | null = null;
      const queryString = isFromChooseYourExtrasCover ? IS_CHOOSE_YOUR_EXTRAS_COVER : IS_CHOOSE_YOUR_COVER;

      if (isFromChooseYourExtrasCover) {
        // Go to the extras product page
        cartProductId = selectedProducts.extras;
      } else {
        // Go to the combined product page
        cartProductId = ProductService.getCombinedId(
          { id: selectedProducts.hospital, type: ProductTypeType.Hospital } as ProductType,
          { id: selectedProducts.extras, type: ProductTypeType.Extras } as ProductType,
        );
      }

      const productUrl = `${UrlService.getProductUrl(cartProductId)}?${queryString}=true`;
      router.push(productUrl);
    }
  };

const setActiveStep = (activeStep: string) => (dispatch: Dispatch) =>
  dispatch({ activeStep, type: BUILD_YOUR_COVER_STEP_CHANGE });

export default {
  setActiveStep,
  setOrUnsetSelectedProduct,
  setSelectedServices,
  submitReview,
};
